// Merged "part" geometry (WP5 draw-call budget, §29.4): a whole rig or prop is ONE indexed,
// skinned geometry. Every vertex is bound (weight 1) to the bone of the rigid part that moves it,
// or to the chain-ring bone that sweeps it (noodle arms, strings, ropes). One SkinnedMesh + one
// inverted hull per object, and because the parts are ordinary three.js skinning, any material
// (a white hit flash, a highlight outline) still poses them correctly.
//
// Per-vertex attributes:
//   position, normal  bind space (part-local); chain vertices store (cos, sin, 0) on a unit ring
//   color             linear base color
//   skinIndex/Weight  the part bone (weight 1)
//   aInfo             (shading mode, outline weight 0..1, casts shadow 0/1, solid on ghosts 0/1)
//   aPat              (packed sRGB secondary color, pattern coord u, v, pattern kind)
//   aOutlineNormal    smoothed normal for crack-free inverted hulls on hard-edged primitives
// Indices are ordered with every outlined primitive first, so the hull draws a prefix of them.
import {
  BufferAttribute, BufferGeometry, Color, Matrix3, Matrix4, Sphere, Uint16BufferAttribute, Vector3,
  type ColorRepresentation,
} from 'three';
import { packColor } from './colors';

/** Shading modes understood by the part material (keep in sync with partMaterial.ts). */
export const MODE = {
  BODY: 0, VCOLOR: 1, TINT: 2, IDENT: 3, IDENT_UNLIT: 4, UNLIT: 5, GLYPH: 6, PUPIL: 7, EYE: 8,
  GOLD: 9, GLASS: 10, TEAM_UNLIT: 11, DANGER: 12, POTATO: 13, SPARK: 14, DECAL: 15, SUGAR: 16,
  CARDBOARD: 17, GLOW: 18,
} as const;
export type Mode = (typeof MODE)[keyof typeof MODE];

/** Procedural surface patterns for non-body parts (aPat.w). */
export const PAT = { NONE: 0, STRIPES: 1, DOTS: 2, UV: 3, STARS: 4, WEDGES: 5, BAND: 6, GLOW_BAND: 7 } as const;

export interface PartStyle {
  part: number;
  mode: Mode;
  color?: ColorRepresentation;
  color2?: ColorRepresentation;
  pat?: number;
  /** Stripes per meter along `axis`; dot rows per meter vertically. */
  freq?: number;
  /** Dots / wedges / stars around the circumference. */
  around?: number;
  axis?: 0 | 1 | 2;
  /** Hull weight; 0 = no outline (pupils, decals, glows). Default 1. */
  outline?: number;
  /** Casts into the shadow map (default: lit modes do, unlit decor does not). */
  shadow?: boolean;
  /** GLASS only: base opacity before the fresnel term (default 0.12). */
  alpha?: number;
  /** Keeps its own look on a ghost (eyes, the pebble-ready rock): no tint, no dither. */
  solidGhost?: boolean;
}

const UNLIT_MODES: ReadonlySet<number> = new Set<number>([
  MODE.IDENT_UNLIT, MODE.UNLIT, MODE.GLYPH, MODE.TEAM_UNLIT, MODE.DANGER, MODE.SPARK, MODE.GLOW, MODE.GLASS,
]);

const tmpV = new Vector3();
const tmpN = new Vector3();
const normalMat = new Matrix3();
const tmpColor = new Color();

export interface PartGeometry {
  /** Every primitive (the main mesh). */
  geometry: BufferGeometry;
  /** The same buffers drawing only the outlined prefix (the hull mesh). */
  hull: BufferGeometry;
  vertices: number;
  triangles: number;
  hullTriangles: number;
}

export class PartGeometryBuilder {
  private pos: number[] = [];
  private nrm: number[] = [];
  private col: number[] = [];
  private skin: number[] = [];
  private info: number[] = [];
  private pat: number[] = [];
  private onrm: number[] = [];
  private outlined: number[] = [];
  private plain: number[] = [];

  get vertexCount(): number {
    return this.pos.length / 3;
  }

  /**
   * Appends `geo`, transformed by `matrix` into the part's bind space. Pattern coordinates are
   * taken in that bind space. The source geometry is disposed.
   */
  add(geo: BufferGeometry, matrix: Matrix4 | null, style: PartStyle): this {
    const base = this.vertexCount;
    if (!geo.getAttribute('normal')) geo.computeVertexNormals();
    const p = geo.getAttribute('position');
    const n = geo.getAttribute('normal');
    const c = geo.getAttribute('color');
    const uv = geo.getAttribute('uv');
    if (matrix) normalMat.getNormalMatrix(matrix);
    const color = tmpColor.set(style.color ?? '#FFFFFF');
    const c2 = style.color2 !== undefined ? packColor(style.color2) : 0;
    const kind = style.pat ?? 0;
    const freq = style.freq ?? 1;
    const axis = style.axis ?? 1;
    const outline = style.outline ?? 1;
    const shadow = style.shadow ?? !UNLIT_MODES.has(style.mode);
    const smooth = smoothNormals(geo, matrix);
    for (let i = 0; i < p.count; i++) {
      tmpV.fromBufferAttribute(p, i);
      if (matrix) tmpV.applyMatrix4(matrix);
      tmpN.fromBufferAttribute(n, i);
      if (matrix) tmpN.applyMatrix3(normalMat).normalize();
      this.pos.push(tmpV.x, tmpV.y, tmpV.z);
      this.nrm.push(tmpN.x, tmpN.y, tmpN.z);
      if (c) this.col.push(c.getX(i), c.getY(i), c.getZ(i));
      else this.col.push(color.r, color.g, color.b);
      this.skin.push(style.part);
      this.info.push(style.mode, outline, shadow ? 1 : 0, style.solidGhost ? 1 : 0);
      let pu = 0;
      let pv = 0;
      if (kind === PAT.STRIPES) pv = tmpV.getComponent(axis) * freq;
      else if (kind === PAT.DOTS || kind === PAT.STARS || kind === PAT.WEDGES) {
        pu = (Math.atan2(tmpV.z, tmpV.x) / (Math.PI * 2) + 0.5) * (style.around ?? 8);
        pv = tmpV.y * freq;
      } else if ((kind === PAT.UV || kind === PAT.BAND || kind === PAT.GLOW_BAND) && uv) {
        pu = uv.getX(i) * 2 - 1;
        pv = uv.getY(i) * 2 - 1;
      }
      if (style.mode === MODE.GLASS) pu = style.alpha ?? 0.12;
      this.pat.push(c2, pu, pv, kind);
      this.onrm.push(smooth[i * 3], smooth[i * 3 + 1], smooth[i * 3 + 2]);
    }
    const list = outline > 0 ? this.outlined : this.plain;
    const index = geo.getIndex();
    if (index) for (let i = 0; i < index.count; i++) list.push(base + index.getX(i));
    else for (let i = 0; i < p.count; i++) list.push(base + i);
    geo.dispose();
    return this;
  }

  /**
   * A tube swept by ring bones `firstRing … firstRing + rings − 1`. Vertices sit on the unit ring
   * (cos a, sin a, 0); each ring bone's matrix places, orients and sizes its ring every frame.
   */
  addChainTube(firstRing: number, rings: number, sides: number, style: PartStyle): this {
    const base = this.vertexCount;
    const color = tmpColor.set(style.color ?? '#FFFFFF');
    const outline = style.outline ?? 1;
    const shadow = style.shadow ?? true;
    for (let r = 0; r < rings; r++) {
      for (let s = 0; s <= sides; s++) {
        const a = (s / sides) * Math.PI * 2;
        const ca = Math.cos(a);
        const sa = Math.sin(a);
        this.pos.push(ca, sa, 0);
        this.nrm.push(ca, sa, 0);
        this.col.push(color.r, color.g, color.b);
        this.skin.push(firstRing + r);
        this.info.push(style.mode, outline, shadow ? 1 : 0, 0);
        this.pat.push(0, 0, 0, 0);
        this.onrm.push(ca, sa, 0);
      }
    }
    const list = outline > 0 ? this.outlined : this.plain;
    const row = sides + 1;
    for (let r = 0; r < rings - 1; r++) {
      for (let s = 0; s < sides; s++) {
        const a = base + r * row + s;
        const b = a + row;
        // Wound so faces point outward for ring frames (n, t × n, t).
        list.push(a, a + 1, b, b, a + 1, b + 1);
      }
    }
    return this;
  }

  build(bounds: Sphere): PartGeometry {
    const g = new BufferGeometry();
    const n = this.vertexCount;
    g.setAttribute('position', new BufferAttribute(new Float32Array(this.pos), 3));
    g.setAttribute('normal', new BufferAttribute(new Float32Array(this.nrm), 3));
    g.setAttribute('color', new BufferAttribute(new Float32Array(this.col), 3));
    const skinIndex = new Uint16Array(n * 4);
    const skinWeight = new Float32Array(n * 4);
    for (let i = 0; i < n; i++) {
      skinIndex[i * 4] = this.skin[i];
      skinWeight[i * 4] = 1;
    }
    g.setAttribute('skinIndex', new Uint16BufferAttribute(skinIndex, 4));
    g.setAttribute('skinWeight', new BufferAttribute(skinWeight, 4));
    g.setAttribute('aInfo', new BufferAttribute(new Float32Array(this.info), 4));
    g.setAttribute('aPat', new BufferAttribute(new Float32Array(this.pat), 4));
    g.setAttribute('aOutlineNormal', new BufferAttribute(new Float32Array(this.onrm), 3));
    const all = this.outlined.concat(this.plain);
    const index = new BufferAttribute(n > 65535 ? new Uint32Array(all) : new Uint16Array(all), 1);
    g.setIndex(index);
    g.boundingSphere = bounds.clone();

    // The hull shares every buffer and draws only the outlined prefix.
    const hull = new BufferGeometry();
    for (const name of Object.keys(g.attributes)) hull.setAttribute(name, g.getAttribute(name));
    hull.setIndex(index);
    hull.setDrawRange(0, this.outlined.length);
    hull.boundingSphere = bounds.clone();
    return { geometry: g, hull, vertices: n, triangles: all.length / 3, hullTriangles: this.outlined.length / 3 };
  }
}

/** Normals averaged over coincident positions (per primitive), so hulls do not crack at hard edges. */
function smoothNormals(geo: BufferGeometry, matrix: Matrix4 | null): Float32Array {
  const p = geo.getAttribute('position');
  const n = geo.getAttribute('normal');
  const out = new Float32Array(p.count * 3);
  const sums = new Map<string, [number, number, number]>();
  const keys: string[] = new Array<string>(p.count);
  for (let i = 0; i < p.count; i++) {
    const key = `${Math.round(p.getX(i) * 1e4)},${Math.round(p.getY(i) * 1e4)},${Math.round(p.getZ(i) * 1e4)}`;
    keys[i] = key;
    const s = sums.get(key);
    if (s) {
      s[0] += n.getX(i);
      s[1] += n.getY(i);
      s[2] += n.getZ(i);
    } else sums.set(key, [n.getX(i), n.getY(i), n.getZ(i)]);
  }
  for (let i = 0; i < p.count; i++) {
    const s = sums.get(keys[i]) ?? [0, 1, 0];
    tmpN.set(s[0], s[1], s[2]);
    if (tmpN.lengthSq() < 1e-10) tmpN.fromBufferAttribute(n, i);
    if (matrix) tmpN.applyMatrix3(normalMat);
    tmpN.normalize();
    out[i * 3] = tmpN.x;
    out[i * 3 + 1] = tmpN.y;
    out[i * 3 + 2] = tmpN.z;
  }
  return out;
}
